import * as React from "react"

import type { ToastProps } from "@/components/ui/toast"
import { toast as baseToast } from "@/hooks/use-toast"
import { adminToast } from "@/lib/toastUtils"

type ToastAdapterOptions = {
  title?: React.ReactNode
  description?: React.ReactNode
  duration?: number
  variant?: ToastProps["variant"]
  onRetry?: () => void
}

type ToastResult = ReturnType<typeof baseToast>

type ToastAdapter = ((message: React.ReactNode | ToastProps, options?: ToastAdapterOptions) => ToastResult) & {
  success: (message: string, options?: ToastAdapterOptions) => void
  error: (message: string, options?: ToastAdapterOptions) => void
  warning: (message: string, options?: ToastAdapterOptions) => void
  info: (message: string, options?: ToastAdapterOptions) => void
  loading: (message: string, options?: ToastAdapterOptions) => ToastResult
  dismiss: (toastId?: string) => void
}

const isToastProps = (value: unknown): value is ToastProps => {
  if (!value || typeof value !== "object") {
    return false
  }

  if (Array.isArray(value) || React.isValidElement(value)) {
    return false
  }

  return "title" in value || "description" in value || "variant" in value
}

const toastAdapter: ToastAdapter = (message, options) => {
  if (isToastProps(message)) {
    return baseToast(message)
  }

  const resolvedTitle =
    options?.title ?? (typeof message === "string" ? message : undefined) ?? "Notification"

  const resolvedDescription =
    options?.description ??
    (typeof message === "string" ? undefined : message) ??
    undefined

  return baseToast({
    title: resolvedTitle,
    description: resolvedDescription,
    duration: options?.duration,
    variant: options?.variant,
  })
}

toastAdapter.success = (message, options) => {
  adminToast.success(message, options)
}

toastAdapter.error = (message, options) => {
  adminToast.error(message, options)
}

toastAdapter.warning = (message, options) => {
  adminToast.warning(message, options)
}

toastAdapter.info = (message, options) => {
  adminToast.info(message, options)
}

toastAdapter.loading = (message, options) => {
  return adminToast.loading(message, options)
}

toastAdapter.dismiss = (toastId) => {
  baseToast.dismiss(toastId)
}

export { toastAdapter as toast }

